/*
 * File: index.tsx
 * Desc: 表单选择组件
 * File Created: 2019-10-09 17:11:26
 * Author: chenghao at <hao.cheng@karakal.com.cn>
 * ------
 * Copyright 2019 - present, karakal
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Select, Input, Spin } from 'antd';
import { FormBaseProps } from '../form-input';
import { FormProps } from 'antd/lib/form';
import { SelectProps, OptionProps } from 'antd/lib/select';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import { PrototypeCheck } from '../utils';
import { debounce } from 'lodash';
import { InputSearchTable } from '..';

interface FormSelectProps
    extends FormBaseProps,
        Pick<FormProps, 'labelCol' | 'wrapperCol' | 'form' | 'className' | 'style'>,
        Omit<SelectProps, 'placeholder'> {
    /** 搜索调用的API */
    fetchApi?: Function;
    /** 是否支持搜索功能 */
    showSearch?: boolean;
    onSelect?: (value: any, option: React.ReactElement<any>, form?: WrappedFormUtils) => void;
    onChange?: (
        value: any,
        option: React.ReactElement<any> | React.ReactElement<any>[],
        form?: WrappedFormUtils
    ) => void;
    onDeselect?: (value: any) => void;
    popupContainerId?: string;
    /** 选择之后弹窗提示确认操作 */
    changeConfirm?: (param: { onCancel: Function; value: any; oldValue: any }) => void;
    /** 是否formdata的接口传参 */
    isFormData?: boolean;
    /** formdata参数的key */
    formDataKey?: string;
    /** api的参数获取回调，通过value生成新的参数 */
    apiParamsCb?: (value: any) => any;
    /** 下拉选项的数据源 */
    options?: any[];
    /** 是否挂载组件时请求接口 */
    mountFetchApi?: boolean;
    /** 是否庞大的下拉option列表 */
    isHugeOptions?: boolean;
    labelInValue?: boolean;
    /** 标准化数据回显 */
    normalizeGetValue?: (value: any) => any;
    //格式化接口返回的数据
    normalizeResult?: (res: any) => any;
    /** 标准化数据设置 */
    normalizeSetValue?: (value: any) => any;
    /** 标准化数据删除 */
    normalizeDeValue?: (values: any, value: any) => any;
    /** 标准化表格选择 */
    normalizeRowValue?: (values: any, value: any) => any;
    /** select组件的props */
    selectProps?: SelectProps;
    tableConfig?: any;
}

const FormSelect = ({
    label,
    labelCol,
    wrapperCol,
    allowClear,
    fetchApi,
    placeholder,
    showSearch,
    onSelect,
    onChange,
    onDeselect,
    mode,
    disabled,
    popupContainerId,
    className,
    form,
    field,
    changeConfirm,
    isFormData,
    formDataKey,
    apiParamsCb,
    options,
    mountFetchApi = true,
    isHugeOptions,
    labelInValue,
    normalizeGetValue,
    normalizeResult,
    normalizeSetValue,
    normalizeDeValue,
    normalizeRowValue,
    selectProps,
    tableConfig,
    ...restProps
}: FormSelectProps) => {
    const { getFieldDecorator, getFieldValue, setFieldsValue } = form as WrappedFormUtils;
    const { initialValue, required, validator } = restProps;
    const [localOptions, setLocalOptions] = useState<any[]>([]);
    const [fetching, setFetching] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string>();
    const tableRef = useRef<any>();
    const isMultipleMode = mode === 'multiple';

    /** 设置所有的Option */
    const setOptionsMemoized = useCallback(
        (options: any[], value?: any) => {
            /** 设置所有的Option */
            function setOptions(options: any[], value?: any) {
                let localOptions: any[];
                // 搜索显示option的处理
                if (showSearch && value) {
                    localOptions = options.reduce((prev: any[], curr: any) => {
                        curr.value.includes(value) && prev.push(setOption(curr));
                        return prev;
                    }, []);
                } else {
                    localOptions = isHugeOptions ? [] : options.map(setOption);
                }

                setLocalOptions(localOptions);
            }
            setOptions(options, value);
        },
        [showSearch, isHugeOptions, setLocalOptions]
    );
    useEffect(() => {
        options && setOptionsMemoized(options);
    }, [options, setOptionsMemoized]);

    /** 获取接口数据 */
    const fetchApiDataMemoized = useCallback(
        (value?: any) => {
            /** 获取接口数据 */
            function fetchApiData(value?: any): void {
                if (!fetchApi) {
                    // if (!options && !tableConfig) throw new Error('options 不能为空');
                    options && setOptionsMemoized(options, value);
                    return;
                }
                let apiParams;
                if (apiParamsCb) {
                    if (isFormData) {
                        apiParams = new FormData();
                        apiParams.append(formDataKey || 'data', JSON.stringify(apiParamsCb(value)));
                    } else {
                        apiParams = apiParamsCb(value);
                    }
                }
                setFetching(true);
                // 调用接口
                fetchApi(apiParams || value).then((datas: any[]) => {
                    // const localOptions: any[] = datas.map(setOption);
                    // setLocalOptions(localOptions);
                    if (normalizeResult) {
                        datas = normalizeResult(datas);
                    }
                    setOptionsMemoized(datas, value);
                    setFetching(false);
                });
            }
            fetchApiData(value);
        },
        [fetchApi, options, apiParamsCb, isFormData, formDataKey, setOptionsMemoized]
    );
    useEffect(() => {
        mountFetchApi && fetchApiDataMemoized();
    }, [fetchApiDataMemoized, mountFetchApi]);

    /** 设置单个Option */
    function setOption(v: any) {
        return (
            <Select.Option key={v.key} value={v.key.toString()} data-original={v}>
                {v.value || v.key}
            </Select.Option>
        );
    }

    const handleSelect = (value: any): void => {
        if (isMultipleMode) return;
        // 排除字符串以外的数据
        if (!PrototypeCheck.isString(value)) value = value.key;
        const { setFieldsValue, getFieldValue } = form as WrappedFormUtils;

        if (!changeConfirm) {
            // 不确认时直接setvalue
            setFieldsValue({ [field]: value });
            return;
        }
        // 确认后继续，取消后恢复
        const oldValue = getFieldValue(field);
        const setValue = (value: any) => setFieldsValue({ [field]: value });
        changeConfirm({
            onCancel: () => setValue(oldValue),
            value,
            oldValue,
        });
    };

    /** 筛选数据 */
    function filterOption(inputValue: string, option: React.ReactElement<OptionProps>) {
        if (!inputValue) return false;
        const { props } = option;
        return (props.children! as string).includes(inputValue);
    }

    const onSearch = debounce(function(value) {
        if (value) {
            // 挂载时不请求搜索时才发请求
            !mountFetchApi && fetchApiDataMemoized(value);
        }
    }, 500);

    function handleChange(value: any) {
        if (isMultipleMode) {
            handleMultipleValue(value);
        }
        if (!value) {
            setFieldsValue({
                [field]: PrototypeCheck.isNumber(value) ? value.toString() : value,
            });
        }
        removeOptions();

        function removeOptions() {
            if (fetchApi && isMultipleMode) {
                setLocalOptions([]);
            }
        }
    }

    function handleMultipleValue(value: any) {
        const { setFieldsValue } = form as WrappedFormUtils;
        setFieldsValue({ [field]: normalizeSetValue ? normalizeSetValue(value) : value });
    }

    function onMultipleDeselect(value: any) {
        if (isMultipleMode) {
            const { setFieldsValue, getFieldValue } = form!;
            const values: any[] = getFieldValue(field);
            setFieldsValue(
                normalizeDeValue
                    ? normalizeDeValue(values, value)
                    : values.filter(val => val.key !== value.key)
            );
        }
        onDeselect && onDeselect(value);
    }

    function onRowCB(_fn: any, row: any) {
        const { setFieldsValue, getFieldValue } = form!;
        const values: any[] = getFieldValue(field);
        setFieldsValue({
            [field]: normalizeRowValue ? normalizeRowValue(values, row) : [...values, row],
        });
    }

    return (
        <Form.Item
            {...{
                label,
                labelCol,
                wrapperCol,
            }}
            className={className}
        >
            {getFieldDecorator(field, {
                initialValue,
                validateTrigger: 'onChange',
                rules: [
                    { required, message: `请选择${label}` },
                    {
                        validator: (rule: any, value: any, callback: Function) => {
                            validator ? validator(rule, value, callback) : callback();
                        },
                    },
                ],
            })(<Input style={{ display: 'none' }} />)}
            {restProps.readOnly ? (
                restProps.render ? (
                    restProps.render(getFieldValue(field), form)
                ) : (
                    <span>{getFieldValue(field)}</span>
                )
            ) : (
                <>
                    <Select
                        {...selectProps}
                        allowClear={allowClear}
                        style={{ width: '100%' }}
                        showSearch={showSearch}
                        placeholder={placeholder || `请选择${label}`}
                        onSearch={(value: any) => {
                            setInputValue(value);
                            onSearch(value);
                            tableRef.current && tableRef.current.setTableVisible(true);
                        }}
                        onSelect={(value: any, option: any) => {
                            onSelect && onSelect(value, option, form);
                            handleSelect(value);
                        }}
                        onChange={(value: any, option: any) => {
                            onChange && onChange(value, option, form);
                            // 点击删除时，清空表单数据
                            handleChange(value);
                            // fetchApiData();
                        }}
                        filterOption={filterOption}
                        optionFilterProp="children"
                        onDeselect={onMultipleDeselect}
                        {...{
                            mode,
                            disabled,
                            ...(labelInValue && { labelInValue }),
                            ...(popupContainerId && {
                                getPopupContainer: () => document.getElementById(popupContainerId)!,
                            }),
                            ...(isMultipleMode && {
                                notFoundContent: fetching ? <Spin size="small" /> : null,
                            }),
                            ...(tableConfig && { open: false }),
                        }}
                        value={
                            normalizeGetValue
                                ? normalizeGetValue(getFieldValue(field))
                                : PrototypeCheck.isNumber(getFieldValue(field)) ? getFieldValue(field).toString() : getFieldValue(field)
                        }
                    >
                        {localOptions}
                    </Select>
                    {tableConfig && (
                        <InputSearchTable
                            onRowCB={onRowCB}
                            inputValue={inputValue}
                            tableConfig={tableConfig}
                            ref={tableRef}
                        />
                    )}
                </>
            )}
        </Form.Item>
    );
};

export default FormSelect;
